import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BookOpen, Search, Loader2, ChevronRight, ArrowLeft, Clock, Layers, AlertTriangle, RefreshCw } from 'lucide-react';
import { getHelpArticles, getHelpArticle } from '../services/api';

const CATEGORY_META = {
  'getting-started': { label: 'Getting Started', icon: '🚀' },
  analytics:         { label: 'Analytics & Risk', icon: '📊' },
  trading:           { label: 'Trading Tools', icon: '⚡' },
  community:         { label: 'Community', icon: '👥' },
  launch:            { label: 'Token Launch (Creators)', icon: '🪙' },
  account:           { label: 'Account', icon: '🔐' },
};

const DIFF_COLORS = {
  beginner: 'text-emerald-400 bg-emerald-500/10',
  intermediate: 'text-blue-400 bg-blue-500/10',
  advanced: 'text-violet-400 bg-violet-500/10',
  expert: 'text-amber-400 bg-amber-500/10',
};

export default function HelpCenterPage() {
  const [params, setParams] = useSearchParams();
  const articleId = params.get('article');
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Track load failures so the user sees a real error instead of an empty page
  // that makes the search input look broken.
  const [loadError, setLoadError] = useState(null);

  function loadArticles() {
    setLoading(true);
    setLoadError(null);
    getHelpArticles()
      .then(d => setArticles(d.articles || []))
      .catch(err => setLoadError(err?.message || 'Could not load help articles'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadArticles(); }, []);

  useEffect(() => {
    if (articleId) {
      setLoading(true);
      setLoadError(null);
      getHelpArticle(articleId)
        .then(setArticle)
        .catch(err => { setArticle(null); setLoadError(err?.message || 'Could not load this article'); })
        .finally(() => setLoading(false));
    } else {
      setArticle(null);
      setLoadError(null);
    }
  }, [articleId]);

  if (loading && !articles.length) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }

  // Article view
  if (articleId && article) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button onClick={() => setParams({})} className="text-gray-400 hover:text-white text-xs flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to all articles
        </button>
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl">{article.icon}</span>
            <div>
              <h1 className="text-2xl font-bold text-white">{article.title}</h1>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime} min read</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${DIFF_COLORS[article.difficulty] || ''}`}>{article.difficulty}</span>
              </div>
            </div>
          </div>
          <div className="space-y-6 mt-6">
            {(article.sections || []).map(section => (
              <div key={section.id}>
                <h2 className="text-lg font-bold text-white mb-2">{section.title}</h2>
                <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{section.content}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filtered = articles.filter(a =>
    !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.category.toLowerCase().includes(search.toLowerCase())
  );
  const byCategory = filtered.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center"><BookOpen className="w-5 h-5 text-white" /></div>
          Help Center
        </h1>
        <p className="text-gray-500 text-sm mt-1">Master every feature. Become an expert. Trade with confidence.</p>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guides, tutorials, FAQs..."
          className="w-full bg-dark-800 border border-dark-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-600 focus:border-amber-500 focus:outline-none" />
      </div>

      {/* Load error — show retry option instead of an empty page that makes search look broken */}
      {loadError && articles.length === 0 && !loading && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-300">Couldn't load help articles</p>
            <p className="text-xs text-red-300/80 mt-0.5">{loadError}. The search bar will stay empty until the articles load.</p>
          </div>
          <button onClick={loadArticles} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 hover:bg-red-500/10">
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}

      {/* No-match state — distinguish from load failure */}
      {!loadError && articles.length > 0 && filtered.length === 0 && search && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center mb-6">
          <Search className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No articles match "{search}"</p>
          <p className="text-xs text-gray-600 mt-1">Try a different keyword or clear the search to browse all {articles.length} guides.</p>
        </div>
      )}

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
            <span>{CATEGORY_META[cat]?.icon || '📁'}</span>
            {CATEGORY_META[cat]?.label || cat}
            <span className="text-gray-600 text-xs font-normal">({items.length})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(a => (
              <button key={a.id} onClick={() => setParams({ article: a.id })}
                className="bg-dark-800 rounded-xl border border-dark-700/50 p-4 text-left hover:border-amber-400/30 transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-2xl">{a.icon}</span>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{a.title}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-gray-500 text-[10px] flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {a.readTime} min</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${DIFF_COLORS[a.difficulty] || ''}`}>{a.difficulty}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
